import { LoadingContainer } from 'peach/components'
import { useHistory, useParams } from 'peach/router'

import { DynamicForm, Page, PageHeader } from 'core/components'
import { useCreditAgencyCreate } from 'core/types'
import type { RequestBodyCreditAgencyPost } from 'core/types/generated/credit-agencies/types'
import { addToast } from 'core/stores/toasts'

const CreateCreditAgency = () => {
  const history = useHistory()
  const { companyId } = useParams<{ companyId?: string }>()

  const { mutateAsync: createCreditAgency } = useCreditAgencyCreate({
    companyId,
  })

  const initial: Partial<RequestBodyCreditAgencyPost> = {
    agencyName: undefined,
    country: '',
    dataFormat: 'metro2',
    nameFilePerAgencyConvention: true,
    fileNamePrefix: '',
    isPeachAggregator: false,
    sftpAccessMethod: 'usernamePassword',
    sftpHostname: '',
    sftpUsername: '',
    sftpPassword: '',
    sftpCdDir: '',
    encryptionMethod: 'GPG',
    publicKey: '',
    uploadFrequency: 'monthly',
    specificDays: [],
    useLoanExternalId: true,
    enrollExistingLoans: false,
    copyKeysFrom: undefined,
  }

  return (
    <LoadingContainer isLoading={false}>
      <Page variant='full'>
        <PageHeader title='Create Credit Agency' />
        <DynamicForm
          data={initial}
          fieldEditMode='all'
          fieldOrder={[
            'agencyName',
            'country',
            'dataFormat',
            'nameFilePerAgencyConvention',
            'fileNamePrefix',
            'sftpAccessMethod',
            'sftpHostname',
            'sftpUsername',
            'sftpPassword',
            'sftpCdDir',
            'encryptionMethod',
            'publicKey',
            'uploadFrequency',
            'specificDays',
            'useLoanExternalId',
            'enrollExistingLoans',
            'copyKeysFrom',
          ]}
          fieldOverrides={{
            specificDays: 'JsonInput',
            agencyName: 'Select',
            sftpAccessMethod: 'Select',
            uploadFrequency: 'Select',
          }}
          omitFields={[
            'id',
            'createdAt',
            'updatedAt',
            'deletedAt',
            'object',
            'companyId',
            'isPeachAggregator',
          ]}
          onSubmit={async (payload: any) => {
            // Convert empty strings to null/undefined for optional fields that don't accept empty strings
            const cleanedPayload: any = {
              ...payload,
              // isPeachAggregator: deprecated, must always be false
              isPeachAggregator: false,
              // sftpCdDir: string (not nullable) - convert null to empty string
              sftpCdDir: payload.sftpCdDir === null ? '' : payload.sftpCdDir,
              sftpHostname:
                payload.sftpHostname === '' ? null : payload.sftpHostname,
              sftpUsername:
                payload.sftpUsername === '' ? null : payload.sftpUsername,
              sftpPassword:
                payload.sftpPassword === '' ? null : payload.sftpPassword,
              // fileNamePrefix: string (not nullable) - convert null/undefined to empty string
              fileNamePrefix:
                payload.fileNamePrefix === null ||
                payload.fileNamePrefix === undefined
                  ? ''
                  : payload.fileNamePrefix,
              encryptionMethod:
                payload.encryptionMethod === '' ||
                payload.encryptionMethod === null
                  ? null
                  : payload.encryptionMethod,
              publicKey: payload.publicKey === '' ? null : payload.publicKey,
              copyKeysFrom:
                payload.copyKeysFrom === '' || payload.copyKeysFrom === null
                  ? undefined
                  : payload.copyKeysFrom,
            }
            try {
              const res = await createCreditAgency(cleanedPayload)
              const creditAgencyId = (res as any)?.data?.id as
                | string
                | undefined
              if (creditAgencyId) {
                addToast(
                  `Credit Agency ${creditAgencyId} created successfully`,
                  'success',
                )
                history.push(
                  `/companies/${companyId}/credit-agencies/${creditAgencyId}`,
                )
              } else {
                addToast(
                  'Credit Agency created, but ID was not returned',
                  'warning',
                )
                history.push(`/companies/${companyId}/credit-agencies`)
              }
            } catch (error: any) {
              addToast(
                error?.message || 'Failed to create Credit Agency',
                'error',
              )
            }
          }}
          selectOptions={{
            agencyName: [
              { label: 'Experian', value: 'experian' },
              { label: 'Equifax', value: 'equifax' },
              { label: 'TransUnion', value: 'transUnion' },
              { label: 'Innovis', value: 'innovis' },
            ],
            sftpAccessMethod: [
              { label: 'Username Password', value: 'usernamePassword' },
              { label: 'Keys', value: 'keys' },
            ],
            uploadFrequency: [{ label: 'Monthly', value: 'monthly' }],
          }}
          submitText='Create Credit Agency'
          validationRules={{
            agencyName: [
              (v: unknown) =>
                (typeof v === 'string' && v.trim().length > 0) ||
                'Agency name is required',
            ],
            country: [
              (v: unknown) =>
                (typeof v === 'string' && v.trim().length > 0) ||
                'Country is required',
              (v: unknown) =>
                (typeof v === 'string' && v.length <= 2) ||
                'Country must be 2 characters or less',
            ],
            dataFormat: [
              (v: unknown) => v === 'metro2' || 'Data format must be metro2',
            ],
            fileNamePrefix: [
              (v: unknown, formState: any) => {
                const agencyName = formState.values?.agencyName
                const nameFilePerAgencyConvention =
                  formState.values?.nameFilePerAgencyConvention
                if (
                  agencyName === 'experian' &&
                  nameFilePerAgencyConvention === true
                ) {
                  return (
                    (typeof v === 'string' && v.trim().length > 0) ||
                    'File name prefix is required when agency is Experian and name file per agency convention is enabled'
                  )
                }
                return true
              },
            ],
            sftpUsername: [
              (v: unknown, formState: any) => {
                const sftpAccessMethod = formState.values?.sftpAccessMethod
                if (sftpAccessMethod === 'usernamePassword') {
                  return (
                    (typeof v === 'string' &&
                      v !== null &&
                      v.trim().length > 0) ||
                    'SFTP username is required when access method is username/password'
                  )
                }
                return true
              },
            ],
            sftpPassword: [
              (v: unknown, formState: any) => {
                const sftpAccessMethod = formState.values?.sftpAccessMethod
                if (sftpAccessMethod === 'usernamePassword') {
                  return (
                    (typeof v === 'string' &&
                      v !== null &&
                      v.trim().length > 0) ||
                    'SFTP password is required when access method is username/password'
                  )
                }
                return true
              },
            ],
            publicKey: [
              (v: unknown, formState: any) => {
                const encryptionMethod = formState.values?.encryptionMethod
                if (encryptionMethod && encryptionMethod !== null) {
                  return (
                    (typeof v === 'string' &&
                      v !== null &&
                      v.trim().length > 0) ||
                    'Public key is required when encryption method is set'
                  )
                }
                return true
              },
            ],
            uploadFrequency: [
              (v: unknown) =>
                v === 'monthly' || 'Upload frequency must be monthly',
            ],
            specificDays: [
              (v: unknown, formState: any) => {
                const uploadFrequency = formState.values?.uploadFrequency
                if (!uploadFrequency || uploadFrequency !== 'monthly') {
                  return true
                }
                if (!Array.isArray(v)) {
                  return 'Specific days must be an array'
                }
                // Allow empty array - it's optional
                if (v.length === 0) {
                  return true
                }
                if (v.length > 1) {
                  return 'Only one specific day is supported for monthly upload frequency'
                }
                const day = v[0]
                if (typeof day !== 'number' || !Number.isInteger(day)) {
                  return 'Specific days must be integers'
                }
                if (day < 1 || day > 31) {
                  return 'Specific day must be between 1 and 31 (1=first day, 31=last day)'
                }
                return true
              },
            ],
          }}
        />
      </Page>
    </LoadingContainer>
  )
}

export default CreateCreditAgency
